import React from 'react';
import styled from '@emotion/styled';
import { LayoutPage, LayoutContent, AcceptedCreditLimit, Errors, DollarAmount, YellowMessage, Form } from 'components';
import { Container, Button, Text, RadioTile, Callout, Grid, Stack, Message } from 'basis';
import { max } from 'lodash';
import { useStoreValue, APOLLO_NETWORK_ERROR, APOLLO_UNKNOWN_ERROR } from 'store';
import { useFormInitialValues, useDeliverApplication, useSteps, useErrorTrackingAndUpdateStore } from 'hooks';
import { useEventTracking } from 'react-event-tracker';
import { submitHandler, getDataLayerElements, getProductInfo } from 'utils';
import { cleanNumberFormat } from 'utils/formatNumber';
import { useHistory } from 'react-router-dom';
import { PrimaryButtonWrapper } from 'components/form/FormButtons';
import { calculateTotalBalanceTransfer, calculateMinimalCreditLimit } from './utils';

const balanceTransferCreditLimitFactor = 0.9;
const FORM_ID = 'creditLimitRequest';
const defaultMaximumCreditLimit = 99999;

export const CreditLimitRequest = () => {
  const [storeState, updateStore] = useStoreValue();
  const { trackEvent } = useEventTracking();
  const history = useHistory();
  const { handleErrorCallback } = useErrorTrackingAndUpdateStore('Next');
  const { isLastDraftStep, toNext, nextStep } = useSteps();
  const initialValues = useFormInitialValues(FORM_ID);

  const { deliver, loading } = useDeliverApplication({
    storeState,
    onSuccess: () => {
      trackEvent({
        event: {
          category: 'application',
          action: 'application-submitted',
          location: 'credit-limit',
          label: 'Next',
          value: storeState?.acquisition?.creditLimitRequest?.creditLimit,
        },
        ...getDataLayerElements({
          ...storeState,
          applicationSubmitted: true,
        }),
      });
      updateStore({
        activeStep: nextStep,
        applicationSubmitted: true,
      });
      history.push(nextStep);
    },
    onError: handleErrorCallback,
  });

  const balanceTransferList = storeState.acquisition?.balanceTransfer?.balanceTransferList;
  const totalBalanceTransfer =
    storeState.acquisition?.balanceTransfer.requestBalanceTransfer === 'yes'
      ? calculateTotalBalanceTransfer(balanceTransferList)
      : 0;
  const minimalCreditLimit =
    totalBalanceTransfer !== 0
      ? max([
          calculateMinimalCreditLimit(totalBalanceTransfer, balanceTransferCreditLimitFactor),
          getProductInfo()?.minCreditLimit,
        ])
      : getProductInfo()?.minCreditLimit;
  const maxCreditLimit = getProductInfo()?.maxCreditLimit;

  const onSubmit = ({ values, errors }) => {
    function submit() {
      const newAcquisition = {
        ...storeState.acquisition,
        creditLimitRequest: {
          creditLimit:
            values.creditLimitOptions === 'ownCreditLimit'
              ? parseInt(cleanNumberFormat(values.newCreditLimit), 10)
              : defaultMaximumCreditLimit,
        },
      };

      updateStore({
        applicationSubmitted: isLastDraftStep,
        acquisition: newAcquisition,
      });

      if (isLastDraftStep) {
        deliver(newAcquisition);
      } else {
        toNext();
      }
    }

    submitHandler({ submit, errors });
  };

  const retrySubmit = () => {
    trackEvent({
      event: {
        category: 'application',
        action: isLastDraftStep ? 'application-submitted' : 'application-navigation',
        location: 'credit-limit-details',
        label: 'Retry',
      },
    });

    deliver(storeState.acquisition);
  };

  if (storeState.applicationErrors) {
    const retry = [APOLLO_NETWORK_ERROR, APOLLO_UNKNOWN_ERROR].includes(storeState.applicationErrors.type)
      ? retrySubmit
      : null;
    return (
      <LayoutPage>
        <Errors applicationRef={storeState.applicationRef} retry={retry} retrying={loading} />
      </LayoutPage>
    );
  }

  return (
    <LayoutPage>
      <Form id={FORM_ID} initialValues={initialValues} onSubmit={onSubmit} hideFormButtons saveDraftOnSessionTimeout>
        {formData => {
          return (
            <LayoutContent rowsGap="0" noSessionTimeout>
              <Stack gap="4">
                {totalBalanceTransfer > 0 && (
                  <>
                    <Callout title="Balance Transfer consideration">
                      <Stack gap="4">
                        You can transfer balances up to 90% of your approved credit limit.
                        <TotalAmountWrapper>
                          <Text textStyle="body2">Requested Total Balance Transfer: </Text>
                          <DollarAmount value={totalBalanceTransfer.toFixed(2)} testId="total-balance-transfer" />
                        </TotalAmountWrapper>
                      </Stack>
                    </Callout>
                  </>
                )}
                <RadioTile
                  name="creditLimitOptions"
                  label="Please make a choice that suits your needs"
                  testId="has-own-credit-limit"
                  showCircles="true"
                  columns={1}
                  color="white"
                  options={[
                    {
                      label: 'Calculate a credit limit for me',
                      value: 'acceptCreditLimit',
                    },
                    {
                      label: 'Choose my own credit limit',
                      value: 'ownCreditLimit',
                    },
                  ]}
                />
                {formData.state.values.creditLimitOptions === 'ownCreditLimit' && (
                  <AcceptedCreditLimit
                    formData={formData}
                    minLimit={minimalCreditLimit}
                    maxCreditLimit={maxCreditLimit}
                    isBTCreditLimit
                    minErrorMessage={
                      totalBalanceTransfer !== 0
                        ? `A minimum credit limit of $${minimalCreditLimit} is required for this balance transfer.`
                        : `Please enter a credit limit amount of $${minimalCreditLimit} or more in $100 increments`
                    }
                  />
                )}
              </Stack>
              {formData.state.values.creditLimitOptions === 'acceptCreditLimit' && (
                <Container margin="8 0">
                  <YellowMessage>
                    <Message testId="yellow-message" severity="warning-or-significant">
                      <Stack>
                        <DisclaimerTitle>
                          <Text textStyle="heading5">Before you continue</Text>
                        </DisclaimerTitle>
                        <Text>
                          We’ll provide a credit limit based on your financial information and our assessment. Your
                          application will still be subject to approval.
                        </Text>
                      </Stack>
                    </Message>
                  </YellowMessage>
                </Container>
              )}

              {formData.state.values.creditLimitOptions === 'ownCreditLimit' && (
                <Container margin="8 0">
                  <YellowMessage>
                    <Message testId="yellow-message" severity="warning-or-significant">
                      <Stack>
                        <DisclaimerTitle>
                          <Text textStyle="heading5">Before you continue</Text>
                        </DisclaimerTitle>
                        {storeState.acquisition?.balanceTransfer.requestBalanceTransfer === 'yes' ? (
                          <Text>
                            Your approved credit limit may be lower than requested. Any balance transfer will be
                            processed only if your approved limit can accommodate it.
                          </Text>
                        ) : (
                          <Text>Your approved credit limit may be lower than requested.</Text>
                        )}
                      </Stack>
                    </Message>
                  </YellowMessage>
                </Container>
              )}

              <Container margin="2 0 0 0">
                <Grid rowsGap="4" colsGap="10" cols="2">
                  <Grid.Item colSpan="all" colSpan-sm="0" alignItems="center">
                    <PrimaryButtonWrapper>
                      <Grid>
                        <Button width="100%" type="submit" loading={loading}>
                          Agree and continue
                        </Button>
                      </Grid>
                    </PrimaryButtonWrapper>
                  </Grid.Item>
                </Grid>
              </Container>
            </LayoutContent>
          );
        }}
      </Form>
    </LayoutPage>
  );
};

const TotalAmountWrapper = styled.div`
  ${props => `
    display: flex;
    justify-content: space-between;
    align-items: center;
    ${`@media (max-width: ${props.theme.screenSize.sm})`} {
      flex-direction: column;
      align-items: flex-start;
      gap: 2px;
    }
  `}
`;

const DisclaimerTitle = styled.div`
  p {
    font-weight: 700;
  }
  margin-bottom: 6px;
`;
